/**
 * Where a case is, and the control that moves it (FR-E1…FR-E3, FR-E6). Split
 * out of `CaseDetail.tsx` in Wave 15C; every rule is still the server's.
 *
 * `can_move_here` per stage, `can_decide_review` and `frozen` come back on the
 * detail payload using the engine's own rule, so a control greys out for
 * exactly the reason the API would refuse. **The freeze is explained, not
 * merely enforced**: a disabled control with no sentence beside it is how a
 * product teaches people it is broken.
 */

import { Alert, Button, Input, Textarea, formatDate } from '@fiveox/ui'
import { Lock, Milestone } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { RecordSection } from '../../components/page'
import {
  type CaseDetail as Detail,
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  blockingTasks,
  cases,
  stageControl,
  statusPair,
} from '../../lib/api/cases'
import { useAction } from './useAction'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-foreground mt-0.5 text-sm font-medium break-words">{children}</dd>
    </div>
  )
}

export function CaseStage({
  detail,
  canUpdate,
  onChanged,
}: {
  detail: Detail
  canUpdate: boolean
  onChanged: () => Promise<void> | void
}) {
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const { error, busy, run } = useAction()

  const control = stageControl(detail)
  const blocking = blockingTasks(detail)
  const status = statusPair(detail.case)

  const act = (fn: () => Promise<unknown>, fallback: string) =>
    run(async () => {
      await fn()
      setReason('')
      setNote('')
      await onChanged()
    }, fallback)

  return (
    <RecordSection
      id="case-stage"
      icon={<Milestone size={18} aria-hidden />}
      title="Where it is"
      description="The stage it is at, and what moves it on."
    >
      <div className="space-y-5">
        {error && <Alert tone="error">{error}</Alert>}

        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Fact label="Stage">{status.firm}</Fact>
          <Fact label="Category">{CATEGORY_LABELS[detail.case.category]}</Fact>
          {/* Two statuses, and the second is only shown when it says something
              different (ADR-0009 §3). */}
          <Fact label="Your client sees">
            {status.client ?? detail.case.client_status ?? '—'}
          </Fact>
          <Fact label="Priority">{PRIORITY_LABELS[detail.case.priority]}</Fact>
          <Fact label="Period">{detail.case.period ?? '—'}</Fact>
          <Fact label="Workflow">v{detail.workflow.version ?? '—'}</Fact>
          <Fact label="Opened">
            {detail.case.opened_at ? formatDate(detail.case.opened_at) : '—'}
          </Fact>
        </dl>

        {detail.frozen && detail.review && (
          <Alert tone="warning">
            <span className="inline-flex items-center gap-2">
              <Lock size={16} aria-hidden />
              {control.reason}
            </span>
            <div className="mt-2 text-sm">
              Approving sends it to <strong>{detail.review.on_approve ?? '—'}</strong>; sending it
              back goes to <strong>{detail.review.on_reject ?? '—'}</strong>.
            </div>
            {detail.can_decide_review ? (
              <div className="mt-3 space-y-2">
                <Textarea
                  aria-label="Your note on the review"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Why are you sending it back? (required to send it back)"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => cases.decide(detail.review!.id, true, note || undefined),
                        'The review could not be approved.',
                      )
                    }
                  >
                    Approve
                  </Button>
                  <span title={note.trim() ? undefined : 'Write why you are sending it back first.'}>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || !note.trim()}
                      onClick={() =>
                        void act(
                          () => cases.decide(detail.review!.id, false, note),
                          'The review could not be sent back.',
                        )
                      }
                    >
                      Send back
                    </Button>
                  </span>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm">Waiting on {detail.review.reviewer_role ?? 'a reviewer'}.</p>
            )}
          </Alert>
        )}

        {!detail.frozen && canUpdate && (
          <div className="border-border space-y-3 rounded-xl border p-4">
            <p className="text-foreground text-sm font-semibold">Move this case</p>
            {blocking.length > 0 && (
              <Alert tone="info">
                {/* The whole content of that refusal is *which* items are
                    outstanding, so the screen names them rather than greying a
                    control with no explanation. */}
                Finish {blocking.map((t) => `“${t.title}”`).join(', ')} before this case can leave{' '}
                {status.firm}.
              </Alert>
            )}
            {control.reason && <Alert tone="info">{control.reason}</Alert>}
            <div className="flex flex-wrap gap-2" role="group" aria-label="Move to">
              {control.options.map((stage) => (
                <span
                  key={stage.key}
                  title={
                    stage.can_move_here
                      ? undefined
                      : `Your firm has reserved “${stage.name}” for: ${stage.allowed_roles.join(', ')}`
                  }
                >
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!stage.can_move_here || busy}
                    onClick={() =>
                      void act(
                        () => cases.move(detail.case.id, stage.key, reason || undefined),
                        'The case could not be moved.',
                      )
                    }
                  >
                    {stage.name}
                    {stage.requires_review ? ' (review)' : ''}
                  </Button>
                </span>
              ))}
            </div>
            <Input
              size="sm"
              aria-label="Why are you moving it?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why? (optional, kept on the history)"
            />
          </div>
        )}
      </div>
    </RecordSection>
  )
}
